// Écran « Jouer en ligne » (lot 4b) : overlay DOM du LOBBY multijoueur. Présence des
// joueurs connectés, liste des salons en attente, création d'un salon (carte / mode /
// moines) et « Rejoindre ». Reprend l'esthétique et les conventions d'overlay de « Mes
// niveaux » (public/index.html, classes .on-*, masquage via l'attribut [hidden]).
//
// Ce module fait le lobby ET aiguille la partie. Dès qu'une partie démarre (message
// `start`), il se masque et délègue à startOnlineGame (client/online.ts) en lui passant la
// socket OUVERTE et le message `start` ; il continue de RELAYER à la partie les messages
// qui la concernent (snapshot/pause/resume/gameover/aborted), car la socket lui appartient
// (connect(onMsg, onClose) est branché ici). À la fin (onDone), il se ré-affiche au lobby,
// déjà resynchronisé par les online/rooms que le serveur rediffuse en fin de partie.
//
// RECONNEXION AUTOMATIQUE : si la socket se ferme EN PARTIE (onClose), on arrête la partie
// et on rouvre une socket. Le serveur, voyant le userId encore en pause, renvoie
// start(countdownMs 0) + snapshot + resume ; le `start` relance startOnlineGame et la
// reprise est transparente. Si la reconnexion échoue, game est nul au 2e onClose → on
// bascule en « connexion perdue » (pas de boucle infinie).
//
// CAMPAGNE COOP (Task 6) : ouvert avec opts.autoCreate, l'écran crée AUTOMATIQUEMENT un salon
// campagne dès la 1re entrée au lobby (create_room { campaignId, mode: 'coop', monks: false }),
// puis attend un joueur. L'enchaînement des niveaux est piloté par le serveur : chaque niveau
// suivant arrive comme un NOUVEAU `start`, déjà géré par startGame (arrêt de la partie courante
// puis relance) exactement comme une reprise. online.ts affiche « Niveau X/total » depuis start.campaign.
//
// SÉCURITÉ (revue lot 3, rappelée par la revue T5) : TOUT texte d'origine serveur ou
// utilisateur (pseudos connectés, noms de salons/cartes, auteurs, messages d'erreur) est
// inséré via textContent, JAMAIS innerHTML. Les noms de carte ne sont pas filtrés côté
// charset : un nom piégé ne doit jamais devenir du HTML (risque de XSS stocké).
import { hasSpawnP2, hasSpawnP3 } from '../shared/level'
import { TEST_LEVELS } from '../shared/levels-catalog'
import type { ClientMsg, GameModeNet, RoomInfo, ServerMsg } from '../shared/net-types'
import { apiGetLevel, apiLibrary } from './api'
import { mkById } from './util'
import { type Net, connect } from './net'
import { type OnlineGameHandle, startOnlineGame } from './online'
import { dashPref, setDashPref } from './prefs'

export interface OnlineOpts {
  canvas: HTMLCanvasElement
  sheet: HTMLImageElement
  onQuit: () => void // retour au menu principal (Retour / Échap / perte de connexion)
  // Ouverture depuis une campagne : dès la 1re entrée au lobby, on crée AUTOMATIQUEMENT un salon
  // campagne (create_room { campaignId, mode, monks: false, players }) dans le MODE demandé (coop
  // ou versus/duel) pour le nombre de joueurs choisi (2 par défaut, 3 = base N) et on attend les
  // joueurs manquants. Absent = lobby normal (création manuelle).
  autoCreate?: { campaignId: number; name: string; mode: GameModeNet; players?: number }
}
export interface OnlineHandle { stop(): void }

/** Phases de l'écran : pilotent ce qui est visible (voir render()) et le statut par défaut. */
type Phase = 'connecting' | 'lobby' | 'creating' | 'waiting' | 'playing' | 'disconnected'

export function startOnline(opts: OnlineOpts): OnlineHandle {
  const byId = mkById('Jouer en ligne')
  const root = byId('online')
  const presenceEl = byId('on-presence')
  const statusEl = byId('on-status')
  const listEl = byId('on-list')
  const createBtn = byId<HTMLButtonElement>('on-create')
  const cancelBtn = byId<HTMLButtonElement>('on-cancel')
  const startNowBtn = byId<HTMLButtonElement>('on-start-now')
  const backBtn = byId<HTMLButtonElement>('on-back')
  const form = byId<HTMLFormElement>('on-form')
  const selectEl = byId<HTMLSelectElement>('on-level')
  const monksEl = byId<HTMLInputElement>('on-monks')
  const livesEl = byId<HTMLSelectElement>('on-lives')
  const livesField = byId<HTMLElement>('on-lives-field')
  const eventsEl = byId<HTMLInputElement>('on-events')
  const eventsField = byId<HTMLElement>('on-events-field')
  const dashEl = byId<HTMLInputElement>('on-dash')
  dashEl.checked = dashPref() // Lot D : la case reflete la preference (defaut ON), synchronisee a l'envoi
  const formCreateBtn = byId<HTMLButtonElement>('on-form-create')
  const formCancelBtn = byId<HTMLButtonElement>('on-form-cancel')
  const replayBtn = byId<HTMLButtonElement>('on-replay')

  let stopped = false
  let phase: Phase = 'connecting'
  let net: Net | null = null
  // Partie en ligne en cours (null = au lobby). Tant qu'elle vit, l'overlay est masqué et
  // les messages de partie lui sont relayés ; onKey (Échap) lui laisse la main.
  let game: OnlineGameHandle | null = null
  // Une seule action réseau initiatrice à la fois (créer un salon, rejoindre) : bloque les
  // doubles envois pendant qu'on attend room_created / start / error. Remis à faux à l'issue.
  let busy = false
  // Dernier état de présence et de salons reçu du serveur (mémorisé pour re-render à la demande).
  let pseudos: string[] = []
  let rooms: RoomInfo[] = []
  // Cartes famille éligibles (publiées ET dotées d'un départ J2), chargées à la 1re
  // ouverture du formulaire puis mises en cache pour les ouvertures suivantes. `spawn3` = la carte
  // a AUSSI un départ Joueur 3 (éligible à un salon à 3) : sert au pré-filtre local (base N).
  let familyOptions: { slug: string; label: string; spawn3: boolean }[] | null = null
  // Salon campagne auto-créé UNE seule fois (opts.autoCreate) : après une partie, backToLobby
  // repasse par setPhase('lobby') sans repasser par becomeReady, donc pas de recréation.
  let autoCreated = false
  // Base N (T8) : suis-je l'HÔTE du salon en attente (celui qui a reçu room_created) ? Seul l'hôte
  // voit « Commencer ». Le vestiaire affiche « (n/target) » : membres présents / taille visée. Tant
  // qu'aucun room_state n'est arrivé (l'hôte n'en reçoit pas à la création), on retombe sur la
  // cible DEMANDÉE (pendingTarget) et 1 membre (moi).
  let iAmHost = false
  let pendingTarget = 2
  let waitingMembers = 1
  let waitingTarget = 2

  // SÉCURITÉ : textContent (jamais innerHTML) pour tout ce qui vient du serveur.
  function setStatus(text: string): void { statusEl.textContent = text }

  function renderPresence(): void {
    // textContent : les pseudos viennent du serveur (saisie utilisateur).
    presenceEl.textContent = 'Connectés : ' + (pseudos.length > 0 ? pseudos.join(', ') : 'personne')
  }

  function renderList(): void {
    listEl.replaceChildren()
    if (rooms.length === 0) {
      const p = document.createElement('p')
      p.className = 'on-empty'
      p.textContent = 'Aucun salon ouvert. Crée une partie pour lancer un duel.'
      listEl.appendChild(p)
      return
    }
    for (const r of rooms) listEl.appendChild(buildRoomRow(r))
  }

  function buildRoomRow(room: RoomInfo): HTMLElement {
    const row = document.createElement('div')
    row.className = 'on-row'

    const main = document.createElement('div')
    main.className = 'on-row-main'
    const name = document.createElement('span')
    name.className = 'on-room-name'
    // Campagne : on montre son nom (« Campagne : X ») pour que le joueur qui rejoint la
    // distingue d'un niveau seul ; sinon le nom du niveau. Tout via textContent (revue T6).
    name.textContent = room.campaign ? 'Campagne : ' + room.campaign.name : room.levelName
    main.appendChild(name)
    const meta = document.createElement('span')
    meta.className = 'on-room-meta'
    // Libellés FIXES (aucune saisie utilisateur) : mode + présence des moines.
    meta.textContent = (room.mode === 'versus' ? 'Versus' : 'Coop') + (room.monks ? ' · moines' : ' · sans moines')
    main.appendChild(meta)
    const host = document.createElement('span')
    host.className = 'on-room-host'
    host.textContent = 'par ' + room.host // SÉCURITÉ : pseudo de l'hôte (origine utilisateur)
    main.appendChild(host)
    row.appendChild(main)

    const joinBtn = document.createElement('button')
    joinBtn.type = 'button'
    joinBtn.className = 'on-join'
    joinBtn.textContent = 'Rejoindre'
    joinBtn.addEventListener('click', () => onJoin(room.id))
    row.appendChild(joinBtn)
    return row
  }

  // Montre/masque les sections selon la phase. Les éléments à flex (form, liste) sont
  // gardés sous :not([hidden]) dans le CSS : l'attribut hidden suffit à les masquer.
  function render(): void {
    const showLobby = phase === 'lobby'
    const showPresence = phase === 'lobby' || phase === 'creating' || phase === 'waiting'
    presenceEl.hidden = !showPresence
    createBtn.hidden = !showLobby
    listEl.hidden = !showLobby
    // « Rejouer » n'a de sens qu'au lobby, et seulement si on a déjà créé une partie. Le laisser
    // visible ailleurs (salle d'attente, formulaire) donnerait un bouton qui ne répond pas.
    if (!showLobby || !dernierSalon) replayBtn.hidden = true
    form.hidden = phase !== 'creating'
    cancelBtn.hidden = phase !== 'waiting'
    // « Commencer » : caché par défaut ; révélé par updateWaiting selon hôte/>=2/<target.
    startNowBtn.hidden = true
    if (showPresence) renderPresence()
    if (showLobby) renderList()
  }

  function setPhase(p: Phase): void {
    phase = p
    render()
    if (p === 'connecting') setStatus('Connexion au serveur...')
    else if (p === 'waiting') updateWaiting() // statut « (n/target) » + bouton Commencer (base N)
    else if (p === 'disconnected') setStatus('Connexion perdue, reviens au menu.')
    else setStatus('') // lobby / creating / playing (overlay masqué) : statut vierge
  }

  // Vestiaire (base N, T8) : statut « (n/target) » et visibilité du bouton « Commencer » (réservé
  // à l'HÔTE, dès 2 présents et tant que le salon n'est pas plein). Appelé à l'entrée en attente
  // (setPhase) et à chaque room_state reçu. SÉCURITÉ : setStatus = textContent (jamais innerHTML).
  function updateWaiting(): void {
    if (phase !== 'waiting') { startNowBtn.hidden = true; return }
    const n = waitingMembers, t = waitingTarget
    if (opts.autoCreate) {
      const modeLabel = opts.autoCreate.mode === 'versus' ? 'duel' : 'coop'
      setStatus(`Campagne : ${opts.autoCreate.name} · ${modeLabel}. En attente... (${n}/${t})`)
    } else if (t >= 3) {
      setStatus(`En attente de joueurs... (${n}/${t})`)
    } else {
      setStatus('En attente d\'un joueur...') // 2 joueurs : libellé historique strictement inchangé
    }
    startNowBtn.hidden = !(iAmHost && n >= 2 && n < t)
  }

  // Première entrée au lobby après la connexion initiale (1er online/rooms reçu en phase
  // connecting). Ouvert pour une campagne coop (opts.autoCreate) : on crée AUTOMATIQUEMENT le
  // salon campagne UNE fois (drapeau) puis on attend (room_created → phase waiting ; error →
  // message affiché, on reste dans un lobby fonctionnel). En reconnexion EN PARTIE, le serveur
  // renvoie start AVANT tout online/rooms (resumeRoom) : game est alors déjà posé et cette
  // fonction n'est pas appelée (garde `if (!game)` côté onMsg + drapeau : jamais de doublon).
  function becomeReady(): void {
    setPhase('lobby')
    if (!opts.autoCreate || autoCreated) return
    autoCreated = true
    busy = true
    // Nombre de joueurs de la campagne (2 par défaut, 3 = base N) : mémorisé comme cible visée
    // pour le vestiaire (l'hôte ne reçoit pas de room_state à la création).
    pendingTarget = opts.autoCreate.players ?? 2
    net?.send({ type: 'create_room', campaignId: opts.autoCreate.campaignId, mode: opts.autoCreate.mode, monks: false, players: pendingTarget })
    setStatus('Création du salon de campagne...')
  }

  // --- Réception des messages serveur ---------------------------------------
  function onMsg(msg: ServerMsg): void {
    if (stopped) return
    switch (msg.type) {
      case 'online':
        pseudos = msg.pseudos
        // Présence tenue à jour même pendant une partie (retour lobby instantané) ; on ne
        // redessine que si le lobby est visible (aucune partie en cours).
        if (!game) { if (phase === 'connecting') becomeReady(); else renderPresence() }
        return
      case 'rooms':
        rooms = msg.rooms
        if (!game) { if (phase === 'connecting') becomeReady(); else if (phase === 'lobby') renderList() }
        return
      case 'start':
        // Début de partie OU reprise après reconnexion (countdownMs 0) : on (re)lance la
        // vraie partie (startGame arrête d'abord une partie éventuelle : jamais deux à la fois).
        busy = false
        startGame(msg)
        return
      case 'room_state':
        // Vestiaire (base N, T8) : membres présents + taille visée. Un INVITÉ qui rejoint un salon
        // NON plein reçoit room_state (pas start) : il bascule alors en attente. L'HÔTE, déjà en
        // attente, met simplement à jour son compteur « (n/target) » et le bouton Commencer.
        if (!game) {
          busy = false
          waitingMembers = msg.members.length
          waitingTarget = msg.targetSize
          if (phase !== 'waiting') setPhase('waiting')
          else updateWaiting()
        }
        return
      default:
        break
    }
    // Partie en cours : snapshot/pause/resume/gameover/aborted lui sont destinés.
    if (game) { game.onMsg(msg); return }
    // Sinon (au lobby) : les messages de lobby restants.
    if (msg.type === 'room_created') {
      busy = false
      // Je suis l'HÔTE (base N) : seul l'hôte voit « Commencer ». La cible visée = celle demandée
      // (le serveur n'envoie pas de room_state à la création) ; je suis pour l'instant le seul membre.
      iAmHost = true
      waitingMembers = 1
      waitingTarget = pendingTarget
      // updateWaiting (via setPhase) pose le statut « (n/target) », y compris le rappel de campagne
      // (nom via textContent, jamais innerHTML : origine utilisateur). Sans tiret cadratin.
      setPhase('waiting')
      return
    }
    if (msg.type === 'error') {
      busy = false
      // Erreur serveur (création refusée, salon plein/expiré, déjà dans un salon...) :
      // affichée telle quelle. En création, on ré-arme le bouton Créer pour réessayer.
      setStatus(msg.message)
      // Un « Rejouer » refusé (carte retirée, salon plein) doit pouvoir être retenté : sans ce
      // ré-armement le bouton restait grisé et la seule issue était de repasser par le formulaire.
      replayBtn.disabled = false
      if (phase === 'creating') formCreateBtn.disabled = false
      return
    }
    // snapshot/pause/resume/gameover/aborted hors partie : sans objet, ignorés.
  }

  // Lance la vraie partie : masque l'overlay (le canvas jeu + HUD + commandes tactiles
  // apparaissent) et délègue à startOnlineGame en lui passant la socket ouverte. onDone
  // (tap/Entrée/Échap sur l'écran final) ramène au lobby.
  function startGame(start: Extract<ServerMsg, { type: 'start' }>): void {
    if (!net) return // socket fermée entre-temps : rien à jouer
    game?.stop() // défensif : jamais deux parties simultanées
    phase = 'playing'
    root.hidden = true
    game = startOnlineGame({ canvas: opts.canvas, sheet: opts.sheet, net, start, onDone: backToLobby })
  }

  // Fin de partie : on arrête la partie et on ré-affiche le lobby. Le serveur a rediffusé
  // online+rooms en fin de partie, donc pseudos/rooms sont déjà à jour (setPhase les repeint).
  function backToLobby(): void {
    if (stopped) return
    game?.stop()
    game = null
    iAmHost = false
    root.hidden = false
    setPhase('lobby')
    // On sort d'une partie : la revanche est à un clic. Réservé à qui a CRÉÉ le salon — les
    // autres verront le nouveau apparaître dans la liste, comme au premier tour.
    if (dernierSalon) { replayBtn.hidden = false; replayBtn.disabled = false }
  }

  // --- Formulaire de création ------------------------------------------------
  function onOpenForm(): void {
    if (busy || phase !== 'lobby') return
    formCreateBtn.disabled = false
    setPhase('creating')
    populateSelect()
    syncLivesField()
  }

  function onCloseForm(): void {
    if (phase !== 'creating') return
    setPhase('lobby')
  }

  function populateSelect(): void {
    selectEl.replaceChildren()
    // Catalogue : TOUS éligibles (départ J2 posé au lot 4). value = slug canonique.
    for (const l of TEST_LEVELS) {
      const opt = document.createElement('option')
      opt.value = l.slug
      opt.textContent = l.title // libellé du catalogue (chaîne fixe du code)
      selectEl.appendChild(opt)
    }
    // Cartes famille éligibles : depuis le cache, sinon chargées puis ajoutées.
    if (familyOptions) {
      appendFamilyOptions(familyOptions)
    } else {
      void loadFamilyOptions()
        .then((opts) => {
          familyOptions = opts
          if (stopped || phase !== 'creating') return
          appendFamilyOptions(opts)
        })
        .catch(() => { /* hors ligne / erreur : le catalogue seul reste sélectionnable */ })
    }
  }

  function appendFamilyOptions(opts: { slug: string; label: string }[]): void {
    for (const o of opts) {
      const opt = document.createElement('option')
      opt.value = o.slug
      opt.textContent = o.label // SÉCURITÉ : nom de carte + auteur (origine utilisateur)
      selectEl.appendChild(opt)
    }
  }

  // Bibliothèque famille (cartes publiées de tous) filtrée sur la présence d'un départ J2.
  // La grille (ascii) n'est pas dans la liste : on charge chaque carte pour tester hasSpawnP2.
  async function loadFamilyOptions(): Promise<{ slug: string; label: string; spawn3: boolean }[]> {
    const libs = await apiLibrary()
    const fulls = await Promise.all(libs.map((l) => apiGetLevel(l.id).catch(() => null)))
    const out: { slug: string; label: string; spawn3: boolean }[] = []
    for (const full of fulls) {
      const rows = full ? full.ascii.split('\n') : null
      if (full && rows && hasSpawnP2(rows)) {
        // spawn3 : la carte a AUSSI un départ Joueur 3 (éligible à un salon à 3, base N).
        out.push({ slug: `fam-${full.id}`, label: `${full.name} · par ${full.author}`, spawn3: hasSpawnP3(rows) })
      }
    }
    return out
  }

  function selectedMode(): GameModeNet {
    const checked = form.querySelector('input[name="on-mode"]:checked') as HTMLInputElement | null
    return checked?.value === 'coop' ? 'coop' : 'versus'
  }

  // Nombre de joueurs visé (base N, T8) : 2 (duel, défaut) ou 3. Lu du groupe radio #on-players.
  // Pertinent en versus ET en coop (on le garde donc toujours visible, pas de syncPlayersField).
  function selectedPlayers(): number {
    const checked = form.querySelector('input[name="on-players"]:checked') as HTMLInputElement | null
    return checked?.value === '3' ? 3 : 2
  }

  // La carte choisie a-t-elle un départ Joueur 3 (éligible à un salon à 3) ? Catalogue : réponse
  // SÛRE depuis ses rows. Carte famille : connue si la bibliothèque est déjà chargée ; sinon on
  // laisse le serveur revalider (il renverra l'erreur, affichée telle quelle). Pré-filtre minimal.
  function cardHasThreeSpawns(slug: string): boolean {
    if (slug.startsWith('fam-')) {
      const fam = familyOptions?.find((o) => o.slug === slug)
      return fam ? fam.spawn3 : true
    }
    const cat = TEST_LEVELS.find((l) => l.slug === slug)
    return cat ? hasSpawnP3(cat.rows) : true
  }

  // Les réglages de survie (Vies + Événements) n'ont de sens qu'en versus : masqués en coop
  // (spec §4). Rafraîchis à l'ouverture du formulaire et à chaque changement de mode ('change').
  function syncLivesField(): void {
    const hide = selectedMode() !== 'versus'
    livesField.hidden = hide
    eventsField.hidden = hide
  }

  /**
   * LES RÉGLAGES DE LA DERNIÈRE PARTIE CRÉÉE (06/08) — pour le bouton « Rejouer ».
   *
   * Demande de Claude-Alain : « il faut un bouton rejouer en fin de partie ». En famille on
   * enchaîne les revanches, et il fallait à chaque fois rouvrir le formulaire et re-choisir carte,
   * mode, vies, nombre de joueurs. On mémorise donc l'envoi tel quel et on le rejoue à l'identique.
   *
   * Volontairement côté CLIENT : garder un salon vivant après sa fin toucherait au cycle de vie
   * des salons — le code le plus délicat du serveur (timers, roomByUser, salons fantômes qui
   * bloqueraient un joueur hors de tout autre salon). Ici, rien ne change au protocole : c'est le
   * même `create_room` qu'au premier tour, et les autres joueurs le voient apparaître dans la
   * liste comme d'habitude.
   */
  let dernierSalon: Extract<ClientMsg, { type: 'create_room' }> | null = null

  function onReplay(): void {
    if (busy || phase !== 'lobby' || !dernierSalon) return
    busy = true
    replayBtn.disabled = true
    setStatus('On remet ça...')
    net?.send(dernierSalon)
  }

  function onSubmitCreate(ev: Event): void {
    ev.preventDefault()
    if (busy || phase !== 'creating') return
    const levelSlug = selectEl.value
    if (!levelSlug) { setStatus('Choisis une carte.'); return }
    const players = selectedPlayers()
    // Pré-filtre local (base N) : un salon à 3 exige un départ Joueur 3. On bloque AVANT l'envoi
    // pour les cartes dont on connaît la grille (catalogue, famille chargée) ; le serveur revalide
    // de toute façon (défense en profondeur). Même libellé que l'erreur serveur.
    if (players === 3 && !cardHasThreeSpawns(levelSlug)) {
      setStatus('Cette carte n\'a pas de départ Joueur 3')
      return
    }
    busy = true
    formCreateBtn.disabled = true
    setStatus('Création du salon...')
    // Vies + événements : seulement en versus (le serveur ignore en coop) ; 0/false par défaut.
    const mode = selectedMode()
    const lives = mode === 'versus' ? Number(livesEl.value) || 0 : 0
    const events = mode === 'versus' && eventsEl.checked
    setDashPref(dashEl.checked) // memorise le choix (Lot D) pour le solo et les prochains salons
    pendingTarget = players // cible visée mémorisée pour le vestiaire (pas de room_state à la création)
    dernierSalon = { type: 'create_room', levelSlug, mode, monks: monksEl.checked, lives, events, players, dashEnabled: dashEl.checked }
    net?.send(dernierSalon)
  }

  // --- Rejoindre / annuler ---------------------------------------------------
  function onJoin(roomId: string): void {
    if (busy || phase !== 'lobby') return
    busy = true
    iAmHost = false // en rejoignant, je ne suis pas l'hôte (pas de bouton Commencer)
    setStatus('Connexion à la partie...')
    net?.send({ type: 'join_room', roomId })
  }

  // Annuler un salon en attente : le serveur le retire (hôte) ou me sort du vestiaire (invité) et
  // rediffuse la liste ; on repasse au lobby sans attendre (le prochain message rooms rafraîchira).
  function onCancelWaiting(): void {
    if (phase !== 'waiting') return
    net?.send({ type: 'leave_room' })
    busy = false
    iAmHost = false
    setPhase('lobby')
  }

  // Départ ANTICIPÉ (base N, T8) : bouton « Commencer », réservé à l'hôte d'un salon en attente
  // dès 2 présents. Le bouton n'est visible que dans ce cas (updateWaiting) ; on reste défensif et
  // le serveur revalide (hôte, waiting, >= 2). Ignoré sinon.
  function onStartNow(): void {
    if (phase !== 'waiting' || !iAmHost) return
    net?.send({ type: 'start_now' })
  }

  // --- Sortie de l'écran -----------------------------------------------------
  // Perte de connexion (jamais un départ voulu : net.close() ne rappelle pas onClose).
  function onClose(): void {
    if (stopped) return
    net = null
    if (game) {
      // Coupure EN PARTIE : le serveur met la partie en pause côté adversaire. On tente une
      // reprise AUTOMATIQUE — arrêt de la partie, réouverture de la socket ; si le userId est
      // encore en pause, le serveur renvoie start(countdownMs 0)+snapshot+resume et le `start`
      // relance la partie (voir onMsg). Si la réouverture échoue, on repasse ici avec game
      // nul → « connexion perdue » (aucune boucle infinie).
      game.stop()
      game = null
      root.hidden = false
      setPhase('connecting')
      net = connect(onMsg, onClose)
      return
    }
    setPhase('disconnected')
  }

  function onBack(): void {
    teardown()
    opts.onQuit()
  }
  function onKey(e: KeyboardEvent): void {
    if (game) return // en partie, c'est la partie qui gère Échap (abandon / retour au salon)
    if (e.code === 'Escape') { e.preventDefault(); onBack() }
  }

  function teardown(): void {
    if (stopped) return
    stopped = true
    game?.stop() // coupe raf + interval d'input + écouteurs de la partie
    game = null
    createBtn.removeEventListener('click', onOpenForm)
    cancelBtn.removeEventListener('click', onCancelWaiting)
    startNowBtn.removeEventListener('click', onStartNow)
    backBtn.removeEventListener('click', onBack)
    formCancelBtn.removeEventListener('click', onCloseForm)
    form.removeEventListener('submit', onSubmitCreate)
    form.removeEventListener('change', syncLivesField)
    removeEventListener('keydown', onKey)
    net?.close() // ferme la socket ; done=true côté net, donc onClose ne repart pas
    net = null
    listEl.replaceChildren()
    selectEl.replaceChildren()
    root.hidden = true
  }

  createBtn.addEventListener('click', onOpenForm)
  cancelBtn.addEventListener('click', onCancelWaiting)
  startNowBtn.addEventListener('click', onStartNow)
  backBtn.addEventListener('click', onBack)
  replayBtn.addEventListener('click', onReplay)
  formCancelBtn.addEventListener('click', onCloseForm)
  form.addEventListener('submit', onSubmitCreate)
  form.addEventListener('change', syncLivesField) // masque/affiche « Vies » selon le mode
  addEventListener('keydown', onKey)

  setPhase('connecting')
  root.hidden = false
  net = connect(onMsg, onClose)
  return { stop: teardown }
}
